import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import Select, { components, MenuListProps, InputActionMeta, GroupBase } from 'react-select'

interface Option {
  value: string
  label: string
  [key: string]: any
}

interface LazySelectProps {
  options: Option[]
  value?: Option | Option[] | null
  onChange: (selected: any) => void
  placeholder?: string
  isMulti?: boolean
  isLoading?: boolean
  isDisabled?: boolean
  isClearable?: boolean
  pageSize?: number
  className?: string
  classNamePrefix?: string
  name?: string
  menuPortalTarget?: HTMLElement | null
  styles?: any
  onBlur?: (e: React.FocusEvent) => void
}

const INITIAL_LOAD = 50

const LazySelect: React.FC<LazySelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  isMulti = false,
  isLoading = false,
  isDisabled = false,
  isClearable = true,
  pageSize = 50,
  className = 'react-select-styled react-select-solid react-select-sm',
  classNamePrefix = 'react-select',
  name,
  menuPortalTarget,
  styles,
  onBlur,
}) => {
  const [displayedCount, setDisplayedCount] = useState(INITIAL_LOAD)
  const [searchInput, setSearchInput] = useState('')
  const isLoadingMoreRef = useRef(false)
  const scrollPositionRef = useRef(0)
  const menuListElementRef = useRef<HTMLDivElement | null>(null)

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchInput.trim()) {
      return options
    }
    const searchLower = searchInput.toLowerCase()
    return options.filter((option) =>
      option.label.toLowerCase().includes(searchLower)
    )
  }, [options, searchInput])

  // Get currently displayed options (lazy loaded)
  const displayedOptions = useMemo(() => {
    return filteredOptions.slice(0, displayedCount)
  }, [filteredOptions, displayedCount])

  const hasMore = displayedCount < filteredOptions.length
  const totalCount = filteredOptions.length

  // Load more options
  const loadMore = useCallback(() => {
    if (isLoadingMoreRef.current || displayedCount >= filteredOptions.length) return
    
    isLoadingMoreRef.current = true
    
    // Save scroll position
    if (menuListElementRef.current) {
      scrollPositionRef.current = menuListElementRef.current.scrollTop
    }
    
    setDisplayedCount((prev) => Math.min(prev + pageSize, filteredOptions.length))
    
    // Reset loading flag
    setTimeout(() => {
      isLoadingMoreRef.current = false
    }, 50)
  }, [displayedCount, pageSize, filteredOptions.length])

  // Handle input change for search
  const handleInputChange = useCallback((inputValue: string, actionMeta: InputActionMeta) => {
    if (actionMeta.action === 'input-change') {
      setSearchInput(inputValue)
      setDisplayedCount(INITIAL_LOAD)
      scrollPositionRef.current = 0
    }
  }, [])

  // Reset displayed count when options change
  useEffect(() => {
    setDisplayedCount(INITIAL_LOAD)
    setSearchInput('')
    scrollPositionRef.current = 0
  }, [options.length])

  // Restore scroll position after loading more
  useEffect(() => {
    const el = menuListElementRef.current
    if (el && scrollPositionRef.current > 0) {
      // Use setTimeout to ensure DOM has updated
      setTimeout(() => {
        el.scrollTop = scrollPositionRef.current
      }, 0)
    }
  }, [displayedCount])

  // Scroll handler - attached via ref
  const handleScroll = useCallback(() => {
    const el = menuListElementRef.current
    if (!el) return
    
    const { scrollTop, scrollHeight, clientHeight } = el
    scrollPositionRef.current = scrollTop
    
    // Check if near bottom (within 100px)
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight
    
    if (distanceFromBottom < 100 && !isLoadingMoreRef.current && displayedCount < filteredOptions.length) {
      loadMore()
    }
  }, [displayedCount, filteredOptions.length, loadMore])

  // Setup scroll listener when menu element changes
  useEffect(() => {
    const el = menuListElementRef.current
    if (el) {
      el.addEventListener('scroll', handleScroll)
      return () => el.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  // Custom MenuList with innerRef to capture the DOM element
  const CustomMenuList = (props: MenuListProps<Option, boolean, GroupBase<Option>>) => {
    return (
      <components.MenuList
        {...props}
        innerRef={(element: HTMLDivElement) => {
          menuListElementRef.current = element
          // Attach scroll listener immediately
          if (element) {
            element.onscroll = handleScroll
          }
        }}
      >
        {props.children}
        {displayedCount < totalCount && (
          <div
            style={{
              padding: '8px 12px',
              textAlign: 'center',
              color: '#6c757d',
              fontSize: '11px',
              background: 'linear-gradient(to bottom, #f8f9fa, #e9ecef)',
              borderTop: '1px solid #dee2e6',
              cursor: 'pointer',
            }}
            onClick={(e) => {
              e.stopPropagation()
              loadMore()
            }}
          >
            Showing {displayedCount} of {totalCount} — Click to load more
          </div>
        )}
      </components.MenuList>
    )
  }

  const defaultStyles = {
    control: (provided: any) => ({
      ...provided,
      width: '100%',
      border: '1px solid #e4e6ef',
    }),
    menuList: (provided: any) => ({
      ...provided,
      maxHeight: '250px',
    }),
    ...(menuPortalTarget
      ? {
          menuPortal: (provided: any) => ({
            ...provided,
            zIndex: 10050,
          }),
        }
      : {}),
    ...styles,
  }

  return (
    <Select
      name={name}
      options={displayedOptions}
      value={value}
      onChange={onChange as any}
      placeholder={placeholder}
      isMulti={isMulti}
      isLoading={isLoading}
      isDisabled={isDisabled}
      isClearable={isClearable}
      className={className}
      classNamePrefix={classNamePrefix}
      menuPortalTarget={menuPortalTarget ?? undefined}
      menuPosition={menuPortalTarget ? 'fixed' : undefined}
      styles={defaultStyles}
      inputValue={searchInput}
      onInputChange={handleInputChange}
      onBlur={onBlur}
      components={{ MenuList: CustomMenuList }}
      filterOption={() => true}
      noOptionsMessage={() =>
        isLoading
          ? 'Loading...'
          : searchInput
          ? 'No results found'
          : options.length === 0
          ? 'No options available'
          : 'Start typing to search...'
      }
    />
  )
}

export default LazySelect
